import React, { useEffect, useState } from 'react';

import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import dayjs from 'dayjs';
import { faArrowLeft, faPlay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useHistory, useParams } from 'react-router-dom';

import {
  backgroundGrey,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  modernSalmon,
  modernSalmonBackground,
  positiveGreen,
  shadowVeryLight,
  softAmber,
  softAmberLight,
  softGreenLight,
  softPurple,
  softPurpleLight,
  white,
} from 'lib/css/colors';
import {
  BRAND_SKU_MAPPER_MANUFACTURER,
  BRAND_SKU_MAPPER_START_RUN,
  getURL,
} from 'lib/networking/endpoints';
import {
  BrandSkuMapperRunRequestField,
  BrandSkuMapperRunStatus,
} from 'lib/enums/BrandSkuMapperEnums';
import Container from 'components/shared/Container';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { areYouSure, SwalToast } from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function BrandSkuMapperManufacturerView() {
  const { manufacturerId } = useParams();
  const history = useHistory();

  const [useVision, setUseVision] = useState(true);

  const summaryUrl = getURL(BRAND_SKU_MAPPER_MANUFACTURER, {
    manufacturer_id: manufacturerId,
  });

  const { data, loading, refetch } = useGet(summaryUrl);

  const runs = data?.runs || [];
  const manufacturer = data?.manufacturer;
  const poolSize = data?.mfr_product_pool_size ?? 0;

  const activeRun = runs.find(
    r =>
      r.status === BrandSkuMapperRunStatus.PENDING || r.status === BrandSkuMapperRunStatus.RUNNING,
  );

  // Depend on primitives rather than the whole activeRun object so the effect
  // only re-runs when the run ID or status actually changes, not on every poll
  // cycle that returns a structurally-identical (but freshly-allocated) object.
  const activeRunId = activeRun?.id;
  const activeRunStatus = activeRun?.status;
  const isPolling = Boolean(activeRunId);
  useEffect(() => {
    if (!activeRunId) return undefined;
    const id = setInterval(refetch, 4000);
    return () => clearInterval(id);
  }, [activeRunId, activeRunStatus, refetch]);

  const startRunUrl = getURL(BRAND_SKU_MAPPER_START_RUN, { manufacturer_id: manufacturerId });
  const { postData: dispatchRun, loading: starting } = usePost(
    startRunUrl,
    () => {
      SwalToast('success', 'Analysis started. Results will appear shortly.');
      refetch();
    },
    err => {
      const msg = err?.response?.data?.message || 'Failed to start analysis. Please try again.';
      SwalToast('error', msg);
    },
  );

  const onStart = ({ remainingOnly }) => {
    const baseMessage =
      `This will analyze unmapped branded vendor products against ` +
      `${poolSize.toLocaleString()} verified manufacturer products.`;
    const confirmMessage = remainingOnly
      ? `${baseMessage} Only products that have never been analyzed will be processed.`
      : baseMessage;
    areYouSure(
      () =>
        dispatchRun({
          use_vision: useVision,
          [BrandSkuMapperRunRequestField.REMAINING_ONLY]: remainingOnly,
        }),
      confirmMessage,
      remainingOnly ? 'Run for new products only?' : 'Run for all products?',
    );
  };

  const renderStatus = status => {
    let bg = softPurpleLight;
    let color = softPurple;
    if (status === BrandSkuMapperRunStatus.COMPLETED) {
      bg = softGreenLight;
      color = positiveGreen;
    } else if (
      status === BrandSkuMapperRunStatus.RUNNING ||
      status === BrandSkuMapperRunStatus.PENDING
    ) {
      bg = softAmberLight;
      color = softAmber;
    } else if (status === BrandSkuMapperRunStatus.FAILED) {
      bg = modernSalmonBackground;
      color = modernSalmon;
    }
    return (
      <span className={css(styles.statusPill)} style={{ backgroundColor: bg, color: color }}>
        {status}
      </span>
    );
  };

  if (loading && !data) {
    return <LoadingSpinner />;
  }

  if (!manufacturer) {
    return (
      <Container>
        <div className="text-center mt-5 text-muted">Manufacturer not found.</div>
      </Container>
    );
  }

  return (
    <Container className={css(styles.container)}>
      <Button
        variant="link"
        className={css(styles.backLink)}
        onClick={() => history.push('/enrich-products/brand-sku-mapper')}
      >
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
        Back to manufacturers
      </Button>

      <Card className={css(styles.headerCard)}>
        <Card.Body>
          <Row>
            <Col md={8}>
              <div className="d-flex align-items-center">
                {manufacturer.logo_url ? (
                  <img
                    src={manufacturer.logo_url}
                    alt={manufacturer.name}
                    className={css(styles.logo)}
                    onError={e => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className={css(styles.logoPlaceholder)}>{manufacturer.name?.[0] || '?'}</div>
                )}
                <div>
                  <h3 className={css(styles.mfrTitle)}>{manufacturer.name}</h3>
                  <div className={css(styles.mfrSubtitle)}>Manufacturer #{manufacturer.id}</div>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col md={6}>
              <div className={css(styles.statCard)}>
                <div className={css(styles.statLabel)}>Verified manufacturer products</div>
                <div className={css(styles.statValue)}>{poolSize.toLocaleString()}</div>
              </div>
            </Col>
            <Col md={6}>
              <div className={css(styles.statCard)}>
                <div className={css(styles.statLabel)}>Analyses run</div>
                <div className={css(styles.statValue)}>{runs.length}</div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className={css(styles.runCard)}>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <h5 className={css(styles.sectionTitle)}>Run a new analysis</h5>
              <div className={css(styles.mutedText)}>
                Uses text similarity to pre-filter candidates, then Claude vision (haiku-4-5) to
                rank the top matches. You can disable vision for a text-only preview.
              </div>
              <Form.Check
                className="mt-3"
                type="checkbox"
                id="use-vision"
                label="Use Claude vision ranking (recommended)"
                checked={useVision}
                onChange={e => setUseVision(e.target.checked)}
                disabled={Boolean(activeRun) || starting}
              />
            </Col>
            <Col md={6} className="text-right">
              <Button
                className={css(styles.primaryBtn)}
                onClick={() => onStart({ remainingOnly: false })}
                disabled={Boolean(activeRun) || starting || poolSize === 0}
                title="Re-analyze every unmapped vendor product"
              >
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                {activeRun ? 'Analysis running…' : starting ? 'Starting…' : 'Run for All'}
              </Button>
              <Button
                variant="outline-secondary"
                className="ml-2"
                onClick={() => onStart({ remainingOnly: true })}
                disabled={Boolean(activeRun) || starting || poolSize === 0}
                title="Only analyze products that have never been analyzed before"
              >
                Run for New Only
              </Button>
              {isPolling && (
                <div className={css(styles.mutedText) + ' mt-2'}>
                  Auto-refreshing every 4s while a run is active.
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <h5 className={css(styles.sectionTitle) + ' mt-4'}>Recent runs</h5>
      <Table striped hover className="mt-2">
        <thead className={css(styles.tableHeader)}>
          <tr>
            <th>Run</th>
            <th>Status</th>
            <th className="text-right">Progress</th>
            <th className="text-right">Matched</th>
            <th>Started</th>
            <th>Completed</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {runs.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center text-muted p-4">
                No analyses yet. Start one above.
              </td>
            </tr>
          ) : (
            runs.map(r => (
              <tr key={r.id}>
                <td>#{r.id}</td>
                <td>{renderStatus(r.status)}</td>
                <td className="text-right">
                  {r.processed_count || 0}/{r.total_vendor_products || 0}
                </td>
                <td className="text-right">
                  <Badge className={css(styles.matchedBadge)}>{r.matched_count || 0}</Badge>
                </td>
                <td>
                  {r.started_at ? dayjs(r.started_at).format('YYYY-MM-DD HH:mm') : '—'}
                  <div className={css(styles.mutedText)}>{r.started_by}</div>
                </td>
                <td>{r.completed_at ? dayjs(r.completed_at).format('YYYY-MM-DD HH:mm') : '—'}</td>
                <td className="text-right">
                  <Link
                    to={
                      `/enrich-products/brand-sku-mapper/manufacturers/` +
                      `${manufacturerId}/products`
                    }
                    className={css(styles.openLink)}
                  >
                    Review →
                  </Link>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey,
  },
  backLink: {
    color: coolGrey,
    padding: 0,
    marginBottom: 16,
    ':hover': {
      color: blackberry,
      textDecoration: 'none',
    },
  },
  headerCard: {
    borderLeft: `6px solid ${grape}`,
    borderRadius: 12,
    boxShadow: `0 1px 3px ${shadowVeryLight}`,
  },
  runCard: {
    marginTop: 16,
    borderRadius: 12,
    boxShadow: `0 1px 3px ${shadowVeryLight}`,
  },
  logo: {
    width: 48,
    height: 48,
    borderRadius: 8,
    marginRight: 16,
    objectFit: 'contain',
    backgroundColor: white,
    border: `1px solid ${greyStroke}`,
  },
  logoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 8,
    marginRight: 16,
    backgroundColor: softPurpleLight,
    color: blackberry,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 20,
  },
  mfrTitle: {
    color: blackberry,
    fontWeight: 700,
    margin: 0,
  },
  mfrSubtitle: {
    color: coolGrey,
    fontSize: 13,
  },
  statCard: {
    backgroundColor: softPurpleLight,
    borderRadius: 10,
    padding: 16,
  },
  statLabel: {
    color: coolGrey,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontWeight: 600,
  },
  statValue: {
    color: blackberry,
    fontSize: 28,
    fontWeight: 700,
    marginTop: 4,
  },
  sectionTitle: {
    color: blackberry,
    fontWeight: 700,
  },
  mutedText: {
    color: coolGrey,
    fontSize: 12,
  },
  primaryBtn: {
    backgroundColor: blackberry,
    borderColor: blackberry,
    padding: '10px 20px',
    fontWeight: 600,
    ':hover': {
      backgroundColor: grape,
      borderColor: grape,
    },
  },
  tableHeader: {
    backgroundColor: softPurpleLight,
    color: blackberry,
  },
  matchedBadge: {
    backgroundColor: softPurpleLight,
    color: blackberry,
    fontSize: 13,
    padding: '4px 10px',
  },
  statusPill: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  openLink: {
    color: grape,
    fontWeight: 600,
    textDecoration: 'none',
    ':hover': {
      color: blackberry,
      textDecoration: 'none',
    },
  },
});

export default BrandSkuMapperManufacturerView;
